import { CSSProperties, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PublicBaseView } from "./PublicBaseView";
import { Photo } from "../models_generated";
import { useAsyncEffect } from "../utils/hooks";
import { API_STORE } from "../ApiStore";

interface PaginatedResponse {
  photos: Photo[];
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

const breadcrumbStyle: CSSProperties = {
  marginBottom: "20px",
  fontSize: "14px",
  color: "#666"
};

const gridStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "20px",
  marginTop: "20px"
};

const photoCardStyle: CSSProperties = {
  width: "calc(33.333% - 15px)",
  minWidth: "200px",
  textDecoration: "none",
  color: "inherit"
};

const photoImageStyle: CSSProperties = {
  width: "100%",
  height: "200px",
  objectFit: "cover",
  borderRadius: "8px",
  border: "1px solid #ddd"
};

const photoNameStyle: CSSProperties = {
  marginTop: "8px",
  fontSize: "14px",
  fontWeight: "500",
  color: "#333",
  textAlign: "center"
};

const paginationStyle: CSSProperties = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  marginTop: "30px",
  marginBottom: "30px"
};

const buttonStyle: CSSProperties = {
  padding: "8px 16px",
  border: "1px solid #ddd",
  borderRadius: "4px",
  backgroundColor: "white",
  cursor: "pointer",
  fontSize: "14px"
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: "#f5f5f5",
  color: "#999",
  cursor: "not-allowed"
};

export function PublicCategoryPhotos() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const root = process.env.PUBLIC_URL || "";

  useAsyncEffect(async () => {
    if (!categoryId) return;

    setLoading(true);
    try {
      const responseData = await API_STORE.doFetch(
        "get",
        `/public/category/${categoryId}/photos?page=${page}&limit=10`,
        { jsonBody: null }
      );
      setData(responseData);
    } catch (error) {
      console.error("Error loading photos:", error);
      alert("Greška pri učitavanju fotografija");
    } finally {
      setLoading(false);
    }
  }, [categoryId, page]);

  const handlePrevPage = () => {
    if (data && page > 1) {
      setPage(page - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNextPage = () => {
    if (data && page < data.totalPages) {
      setPage(page + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <PublicBaseView>
      <div style={breadcrumbStyle}>
        <Link to={`${root}/public`}>Početna</Link> &gt; {categoryId}
      </div>

      <h2>Kategorija: {categoryId}</h2>

      {loading ? (
        <p>Učitavanje...</p>
      ) : !data || data.photos.length === 0 ? (
        <p>Nema fotografija u ovoj kategoriji</p>
      ) : (
        <>
          <div style={gridStyle}>
            {data.photos.map((photo) => (
              <Link
                key={photo.id}
                to={`${root}/public/photo/${photo.id}`}
                style={photoCardStyle}
              >
                <img
                  src={photo.thumbnail || photo.image}
                  alt={photo.name || `Photo ${photo.id}`}
                  style={photoImageStyle}
                />
                {photo.name && <div style={photoNameStyle}>{photo.name}</div>}
              </Link>
            ))}
          </div>

          {data.totalPages > 1 && (
            <div style={paginationStyle}>
              <button
                onClick={handlePrevPage}
                disabled={page === 1}
                style={page === 1 ? buttonDisabledStyle : buttonStyle}
              >
                ← Prethodna
              </button>
              <span style={{ fontSize: "14px", color: "#666" }}>
                Stranica {page} od {data.totalPages} ({data.totalCount} fotografija)
              </span>
              <button
                onClick={handleNextPage}
                disabled={page === data.totalPages}
                style={page === data.totalPages ? buttonDisabledStyle : buttonStyle}
              >
                Sljedeća →
              </button>
            </div>
          )}
        </>
      )}
    </PublicBaseView>
  );
}
